<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, onMounted, ref } from 'vue'
import Button from '@/components/partials/Buttons/Button/Button.vue'
import Headline from '@/components/partials/Headline.vue'
import ProductItem from '@/components/Modals/CartChangedModal/ProductItem.vue'
import { CART_CHANGED_MODAL } from '@/types/Modals'
import { useModalsStore } from '@/stores/useModalsStore'
import { useSessionStore } from '@/stores/useSessionStore'
import { useCartStore } from '@/stores/useCartStore'
import { useEstablishmentStore } from "@/stores/ssr/useEstablishmentStore"
import { useEstablishmentHttp } from "@/http/establishmentHttp"

const { t } = useI18n()

const changes = ref({
  changes: [],
  removed: [],
})

const {
  getData,
  toggle,
} = useModalsStore()
const cartStore = useCartStore()

const distribution = computed(() => {
  const type = getData(CART_CHANGED_MODAL).distribution
  const dist = [ 'takeaway', 'delivery', 'on_location' ][type - 1]
  return t(`${dist}`).toLowerCase()
})

const sessionStore = useSessionStore()
const establishmentStore = useEstablishmentStore()
const establishmentHttp = useEstablishmentHttp()

const confirmToggle = async () => {
  const toDistribution = getData(CART_CHANGED_MODAL).distribution
  changes.value.new_distribution = Number(toDistribution)

  sessionStore.setDistribution(toDistribution)
  cartStore.setDistribution(toDistribution)
  await cartStore.applyCartChanges(changes.value)
  await cartStore.fetchBasketInfo()

  toggle(CART_CHANGED_MODAL, false)
}

async function cancelToggle() {
  const fromDistribution = getData(CART_CHANGED_MODAL).old_distribution
  sessionStore.setDistribution(fromDistribution)

  const establishment = establishmentStore.establishment

  if (establishmentStore.establishment) {
    const distributionInfo = await establishmentHttp.establishmentByDistributionType(
        establishment.slug,
        establishment.location.city.country.code.toLowerCase(),
        establishment.location.city.slug,
        fromDistribution,
        sessionStore.location,
    )

    establishmentStore.setEstablishmentDistribution(distributionInfo)
  }

  toggle(CART_CHANGED_MODAL, false)
}

onMounted(() => {
  if (getData(CART_CHANGED_MODAL).changes) {
    changes.value.changes = getData(CART_CHANGED_MODAL).changes
  }

  if (getData(CART_CHANGED_MODAL).removed) {
    changes.value.removed = getData(CART_CHANGED_MODAL).removed
  }
})
</script>

<template>
  <div
      class="modal-cart-changed"
      data-test-id="modal-cart-changed"
  >
    <Headline>
      <div class="modal-cart-changed__title modal-cart-changed__title--lg">
        {{ t('messages.products_not_available') }}
      </div>
    </Headline>

    <div class="modal-cart-changed__products" v-if="changes.removed.length > 0">
      <p class="modal-cart-changed_text">
        {{ t('confirmation-modal.change-distribution-type', { type: distribution }) }}</p><br>
      <div ref="itemsContainer"
           class="cart__items"
      >
        <ProductItem v-for="product in changes.removed"
                     :key="product.product_id"
                     :product="product"
                     class="cart__item"
                     :with-image="false"
                     :with-point="false"
        />
      </div>
    </div>

    <div class="modal-cart-changed__products" v-if="changes.changes.length > 0 && false">
      <p>{{ t('messages.products_changed_for', [ distribution ]) }}</p>
    </div>

    <div class="modal-cart-changed__buttons">
      <Button type="secondary" @click="cancelToggle">
        {{ t('buttons.cancel') }}
      </Button>

      <Button @click="confirmToggle">
        {{ t('buttons.confirm') }}
      </Button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/css/mixins/styling.scss";
@import "@/assets/css/mixins/breakpoints-up.scss";

.modal-cart-changed {
  width: 100%;

  @include lg-up {
    width: 35rem;
  }

  &__title {
    font-weight: 700;
    margin-bottom: 1rem;

    &--lg {
      font-size: 1.125rem;
      margin-bottom: 0;
    }
  }

  &__products {
    margin: 2rem 0;
  }

  &__product {
    padding: 1rem 0;
    display: flex;
    align-items: center;

    @include icon-svg(1.5rem, 1.5rem) {
      margin-right: 0.5rem;
      color: var(--color-neutral-secondary);
    }
  }

  &__buttons {
    display:    flex;
    justify-content: right;
    margin-top: 1rem;

    > * {
      margin-left: 1rem;
    }

    > button {
      display: none;

      @include lg-up {
        display: block;
      }
    }
  }
}
</style>
